import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRightIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, UserIcon, BoxIcon } from 'lucide-react';
interface ContactStep {
  icon: BoxIcon;
  title: string;
  description: string;
}
const contactSteps: ContactStep[] = [{
  icon: ClockIcon,
  title: 'Revisamos tu mensaje',
  description: 'Leemos tu contexto y detectamos los puntos que conviene abordar primero.'
}, {
  icon: MessageSquareIcon,
  title: 'Definimos la conversación',
  description: 'Te respondemos para entender prioridades, materiales y alcance.'
}, {
  icon: CalendarIcon,
  title: 'Aterrizamos el siguiente paso',
  description: 'Si hay encaje, acordamos la forma más clara de avanzar.'
}];
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormStatus('submitting');
    window.setTimeout(() => {
      setFormStatus('success');
      window.setTimeout(() => setFormStatus('idle'), 4000);
    }, 900);
  };
  const updateField = (field: keyof typeof formData, value: string) => setFormData(current => ({
    ...current,
    [field]: value
  }));
  const inputClass = 'w-full border border-white/15 bg-black px-4 py-3.5 text-sm text-white transition-colors placeholder:text-neutral-600 focus:border-white/60 focus:outline-none focus-visible:ring-2 focus-visible:ring-white';
  return <div className="min-h-screen bg-[#0a0a0a] pt-24" data-id="element-391" data-mp="3lgyycv9sebe6">
      <section className="border-b border-white/10 py-16 sm:py-24" data-id="element-392" data-mp="3lijom8p2j868">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-393" data-mp="3gi5r3kl3fgmg">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-394" data-mp="36tqpi13yai5d">
            <div data-id="element-395" data-mp="31x018qhzsg8v">
              <p className="eyebrow" data-id="element-396" data-mp="3pkhukqgwnmdy">Contacto</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-6xl" data-id="element-397" data-mp="3plsxyp6fus4j">
                Cuéntanos qué necesita tu negocio ahora.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-400 sm:text-lg" data-id="element-398" data-mp="3gr4rwwt1h6eo">
              Completa el formulario y utilizaremos la información para entender
              el punto de partida de tu proyecto. Sin mensajes automatizados ni
              compromisos innecesarios.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell" data-id="element-399" data-mp="21v3igf3uqqyk">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-400" data-mp="21tsi6p9m81c7">
          <div className="grid overflow-hidden border border-white/10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-401" data-mp="1wwjy2j4wpn8b">
            <aside className="flex flex-col bg-black p-8 sm:p-10 lg:border-r lg:border-white/10" data-id="element-402" data-mp="2blg307bo2k4p">
              <p className="eyebrow" data-id="element-403" data-mp="26la9smpr0pki">Después de escribirnos</p>
              <h2 className="mt-5 text-2xl font-medium tracking-[-0.035em] text-white" data-id="element-404" data-mp="26o65s5i1d46b">
                Una primera conversación con contexto.
              </h2>
              <div className="mt-10 space-y-7" data-id="element-405" data-mp="1dyg0mlydtcmc">
                {contactSteps.map(step => {
                const StepIcon = step.icon;
                return <div key={step.title} className="flex gap-4" data-id="element-406" data-mp="1nussrfea3iez">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/15 text-white" data-id="element-407" data-mp="1nxosn99osh62">
                        <StepIcon size={16} aria-hidden="true" data-id="element-408" data-mp="2zar5bt8o0mky" />
                      </div>
                      <div data-id="element-409" data-mp="18tnan4igfrrn">
                        <h3 className="text-sm font-medium text-white" data-id="element-410" data-mp="18uf8yn3fs3lm">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-500" data-id="element-411" data-mp="1duksffzjyjsm">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-auto border-t border-white/10 pt-6" data-id="element-412" data-mp="1rrgf4b8q89r5">
                <p className="flex items-center gap-2 text-xs font-medium text-neutral-300" data-id="element-413" data-mp="1wqt00qg1f6r3">
                  <CheckCircle2Icon size={15} className="text-white" aria-hidden="true" data-id="element-414" data-mp="21ib43ndcxnga" />{' '}
                  Tus datos se tratan de forma confidencial.
                </p>
              </div>
            </aside>

            <motion.form initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.08
          }} onSubmit={handleSubmit} className="bg-[#0a0a0a] p-8 sm:p-10" data-id="element-415" data-mp="2fhkichi067hm">
              <div className="grid gap-5 md:grid-cols-2" data-id="element-416" data-mp="2bmrgu7vjiofv">
                <label className="block" data-id="element-417" data-mp="2bocbrylro9e9">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-418" data-mp="38cic49bnofmq">
                    Nombre completo *
                  </span>
                  <div className="relative" data-id="element-419" data-mp="2ym77slo284fb">
                    <UserIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-420" data-mp="2ykw4so2fv0p9" />
                    <input required value={formData.name} onChange={event => updateField('name', event.target.value)} className={`${inputClass} pl-11`} placeholder="Tu nombre" data-id="element-421" data-mp="3r4um9i8jwl7f" />
                  </div>
                </label>
                <label className="block" data-id="element-422" data-mp="1amljnd68ylu6">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-423" data-mp="1fmzhy3i1qhf0">
                    Negocio *
                  </span>
                  <input required value={formData.business} onChange={event => updateField('business', event.target.value)} className={inputClass} placeholder="Nombre de tu negocio" data-id="element-424" data-mp="16n312o1wemui" />
                </label>
                <label className="block" data-id="element-425" data-mp="2m4mko6m51b2r">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-426" data-mp="2r3g22a4jpm87">
                    Correo electrónico *
                  </span>
                  <div className="relative" data-id="element-427" data-mp="aghuv1fs6dyi">
                    <MailIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-428" data-mp="ahsuqq63v1kc" />
                    <input required type="email" value={formData.email} onChange={event => updateField('email', event.target.value)} className={`${inputClass} pl-11`} placeholder="tu@email.com" data-id="element-429" data-mp="kdcph98xnu7i" />
                  </div>
                </label>
                <label className="block" data-id="element-430" data-mp="39vd1wigwmppl">
                  <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-431" data-mp="39weif2gow4ep">
                    Teléfono *
                  </span>
                  <div className="relative" data-id="element-432" data-mp="2j44hfg8vzcew">
                    <PhoneIcon size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-neutral-600" aria-hidden="true" data-id="element-433" data-mp="2j19zpwh9c5p6" />
                    <input required type="tel" value={formData.phone} onChange={event => updateField('phone', event.target.value)} className={`${inputClass} pl-11`} placeholder="+34 600 000 000" data-id="element-434" data-mp="2xs940vb2882y" />
                  </div>
                </label>
              </div>
              <label className="mt-5 block" data-id="element-435" data-mp="2f3c7eks6x3hk">
                <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-436" data-mp="3li3fb8fqdol3">
                  Sector o actividad *
                </span>
                <select required value={formData.service} onChange={event => updateField('service', event.target.value)} className={inputClass} data-id="element-437" data-mp="3qir4dokefvco">
                  <option value="" disabled data-id="element-438" data-mp="1526p710f17di">
                    Selecciona una opción
                  </option>
                  <option value="hosteleria" data-id="element-439" data-mp="102kdwx45e17c">Hostelería</option>
                  <option value="servicios" data-id="element-440" data-mp="v6n1h1w09ezu">Servicios locales</option>
                  <option value="comercio" data-id="element-441" data-mp="r3gvlvct1ptw">Comercio</option>
                  <option value="profesional" data-id="element-442" data-mp="r632rtvsa3bl">
                    Marca personal o profesional
                  </option>
                  <option value="otro" data-id="element-443" data-mp="h5spk99r0vwf">Otro</option>
                </select>
              </label>
              <label className="mt-5 block" data-id="element-444" data-mp="2giwqq6tw9b0s">
                <span className="mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-500" data-id="element-445" data-mp="2ghloibg3kbbs">
                  Sobre el proyecto{' '}
                  <span className="normal-case tracking-normal text-neutral-600" data-id="element-446" data-mp="xf54157tjds6">
                    (opcional)
                  </span>
                </span>
                <textarea rows={5} value={formData.message} onChange={event => updateField('message', event.target.value)} className={`${inputClass} resize-none`} placeholder="Qué quieres mejorar, qué necesitas ordenar o qué objetivo tienes en mente." data-id="element-447" data-mp="sg29iepgspyg" />
              </label>
              <button type="submit" disabled={formStatus !== 'idle'} className="mt-7 flex w-full items-center justify-center gap-2 bg-white px-5 py-4 text-sm font-semibold text-black transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-448" data-mp="9nzqiwpcoxl1">
                {formStatus === 'idle' && <>
                    Enviar consulta{' '}
                    <ArrowRightIcon size={16} aria-hidden="true" data-id="element-449" data-mp="3hstkzm6ca71y" />
                  </>}
                {formStatus === 'submitting' && 'Enviando...'}
                {formStatus === 'success' && 'Consulta recibida'}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-neutral-600" data-id="element-450" data-mp="3vqqm8vvqn1my">
                Al enviar, aceptas que utilicemos esta información para
                responder a tu consulta.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </div>;
}